// v10/src/components/admin/lifespan/AttachmentModal.js
//
// Modal "Formulario 337" del módulo Vida límite (A6.1) — Tarea 2.
// Maneja: subir, ver, reemplazar y eliminar el attachment del 337.
//
// Endpoints:
//   POST   /api/aircraft-components/:id/attachment  → body { base64, mediaType }
//   DELETE /api/aircraft-components/:id/attachment
//
// El backend (cloudinaryUploadService.js) acepta base64 puro o data URI completo.
// Acá enviamos base64 puro + mediaType — más eficiente y consistente con el resto del codebase.

import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Loader, Upload, FileText, Image as ImageIcon, Trash2, ExternalLink, RefreshCw, CheckCircle2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import componentLifespanService from '../../../services/componentLifespanService';

const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

const formatFileSize = (bytes, t) => {
  if (bytes < 1024 * 1024) {
    return t('component_attachment_modal_size_kb', { kb: (bytes / 1024).toFixed(0) });
  }
  return t('component_attachment_modal_size_mb', { mb: (bytes / (1024 * 1024)).toFixed(2) });
};

// Convierte un File a base64 puro (sin el prefijo "data:...;base64,").
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    const idx = result.indexOf('base64,');
    resolve(idx >= 0 ? result.slice(idx + 'base64,'.length) : result);
  };
  reader.onerror = () => reject(reader.error || new Error('FileReader error'));
  reader.readAsDataURL(file);
});

// Construye un nombre de archivo legible para el atributo download del link.
// Ej: "337_Motor_Continental_O_dddc5648.pdf"
const buildDownloadName = (component, attachmentType, attachmentUrl) => {
  const rawName = component?.name || 'componente';
  const safeName = rawName
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 60) || 'componente';
  const idTail = (component?.id || '').slice(0, 8);
  let ext = attachmentType === 'pdf' ? 'pdf' : 'jpg';
  // Si la URL termina con una extensión conocida, usarla
  const urlExt = String(attachmentUrl || '').match(/\.([a-zA-Z0-9]{3,4})(?:\?.*)?$/);
  if (urlExt) {
    const found = urlExt[1].toLowerCase();
    if (['pdf', 'jpg', 'jpeg', 'png', 'webp'].includes(found)) ext = found;
  }
  return `337_${safeName}${idTail ? '_' + idTail : ''}.${ext}`;
};

const AttachmentModal = ({ component, onSuccess, onClose, darkMode }) => {
  const { t } = useTranslation('admin');

  const [attachmentUrl, setAttachmentUrl] = useState(component?.attachment_url || null);
  const [attachmentType, setAttachmentType] = useState(component?.attachment_type || null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const inputRef = useRef(null);
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const busy = uploading || deleting;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = setTimeout(() => {
      if (isMounted.current) setSuccessMessage(null);
    }, 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const validateFile = (file) => {
    if (!file) return t('lifespan_validation_attachment_type');
    if (!ALLOWED_MIME_TYPES.includes(file.type)) return t('lifespan_validation_attachment_type');
    if (file.size > MAX_BYTES) return t('lifespan_validation_attachment_size');
    return '';
  };

  const handleFilePicked = (file) => {
    setError('');
    if (!file) {
      setSelectedFile(null);
      setValidationError('');
      return;
    }
    const err = validateFile(file);
    if (err) {
      setSelectedFile(null);
      setValidationError(err);
      return;
    }
    setSelectedFile(file);
    setValidationError('');
  };

  const onPickClick = () => {
    if (busy) return;
    inputRef.current?.click();
  };

  const onFileInputChange = (e) => {
    const file = e.target.files?.[0] || null;
    handleFilePicked(file);
    // permitir volver a elegir el mismo archivo
    if (e.target) e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    const file = e.dataTransfer?.files?.[0] || null;
    handleFilePicked(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || busy || !component?.id) return;
    const wasReplace = !!attachmentUrl;
    setUploading(true); setError('');
    try {
      const base64 = await fileToBase64(selectedFile);
      const res = await componentLifespanService.uploadAttachment(component.id, {
        base64,
        mediaType: selectedFile.type,
      });
      if (!isMounted.current) return;
      const data = res?.data?.data || {};
      setAttachmentUrl(data.attachment_url || null);
      setAttachmentType(data.attachment_type || null);
      setSelectedFile(null);
      setSuccessMessage(
        wasReplace
          ? t('component_attachment_modal_replace_success')
          : t('component_attachment_modal_upload_success'),
      );
      onSuccess?.();
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.response?.data?.message || t('lifespan_validation_network_error'));
    } finally {
      if (isMounted.current) setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (busy || !component?.id) return;
    setDeleting(true); setError('');
    try {
      await componentLifespanService.deleteAttachment(component.id);
      if (!isMounted.current) return;
      setAttachmentUrl(null);
      setAttachmentType(null);
      setConfirmDelete(false);
      setSuccessMessage(t('component_attachment_modal_delete_success'));
      onSuccess?.();
    } catch (err) {
      if (!isMounted.current) return;
      setError(err.response?.data?.message || t('lifespan_validation_network_error'));
    } finally {
      if (isMounted.current) setDeleting(false);
    }
  };

  // ── Estilos ──────────────────────────────────────────────────────
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';

  const hasAttachment = !!attachmentUrl;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={busy ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 640, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: 'linear-gradient(135deg,#0ea5e9,#0284c7)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 6px 16px rgba(14,165,233,0.3)',
              }}
            >
              <FileText style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {t('component_attachment_modal_title')}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted }}>
                {t('component_attachment_modal_subtitle', { name: component?.name || '' })}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose} disabled={busy}
            aria-label="close"
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Banner de éxito (auto-dismiss 3s) */}
        <AnimatePresence>
          {successMessage && (
            <motion.div
              key="attachment-success-banner"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', borderRadius: 12, marginBottom: 14,
                background: 'rgba(22,163,74,0.10)',
                border: '1px solid rgba(22,163,74,0.30)',
                color: '#16a34a',
              }}
            >
              <CheckCircle2 style={{ width: 16, height: 16, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 700 }}>{successMessage}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Vista con attachment existente */}
        {hasAttachment && !confirmDelete && (
          <div>
            <div
              style={{
                borderRadius: 14, overflow: 'hidden', marginBottom: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                background: darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
              }}
            >
              {attachmentType === 'image' ? (
                <img
                  src={attachmentUrl}
                  alt={component?.name || ''}
                  style={{ width: '100%', maxHeight: 380, objectFit: 'contain', display: 'block' }}
                />
              ) : (
                <div
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexDirection: 'column', gap: 10, padding: '40px 20px',
                  }}
                >
                  <FileText style={{ width: 56, height: 56, color: '#0284c7' }} />
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: fgPrimary }}>
                    {t('component_attachment_modal_pdf_preview_label')}
                  </p>
                  <a
                    href={attachmentUrl}
                    download={buildDownloadName(component, attachmentType, attachmentUrl)}
                    target="_blank" rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '8px 14px', borderRadius: 10,
                      background: 'linear-gradient(135deg,#0ea5e9,#0284c7)',
                      color: 'white', textDecoration: 'none',
                      fontSize: 13, fontWeight: 700,
                    }}
                  >
                    <ExternalLink style={{ width: 14, height: 14 }} />
                    {t('component_attachment_modal_btn_open')}
                  </a>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {attachmentType === 'image' && (
                <a
                  href={attachmentUrl}
                  download={buildDownloadName(component, attachmentType, attachmentUrl)}
                  target="_blank" rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '10px 14px', borderRadius: 12,
                    background: darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                    color: fgPrimary, textDecoration: 'none',
                    fontSize: 13, fontWeight: 700,
                  }}
                >
                  <ExternalLink style={{ width: 14, height: 14 }} />
                  {t('component_attachment_modal_btn_open')}
                </a>
              )}
              <motion.button
                type="button"
                onClick={onPickClick}
                disabled={busy}
                whileHover={!busy ? { scale: 1.02 } : {}}
                whileTap={!busy ? { scale: 0.97 } : {}}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '10px 14px', borderRadius: 12, border: 'none',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
                  color: 'white', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                }}
              >
                <RefreshCw style={{ width: 14, height: 14 }} />
                {t('component_attachment_modal_btn_replace')}
              </motion.button>
              <motion.button
                type="button"
                onClick={() => setConfirmDelete(true)}
                disabled={busy}
                whileHover={!busy ? { scale: 1.02 } : {}}
                whileTap={!busy ? { scale: 0.97 } : {}}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '10px 14px', borderRadius: 12, border: 'none',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  background: 'transparent',
                  color: '#ef4444', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                }}
              >
                <Trash2 style={{ width: 14, height: 14 }} />
                {t('component_attachment_modal_btn_delete')}
              </motion.button>
            </div>
          </div>
        )}

        {/* Confirmación de eliminación */}
        {hasAttachment && confirmDelete && (
          <div
            style={{
              padding: 16, borderRadius: 14, marginBottom: 12,
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.25)',
            }}
          >
            <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#dc2626' }}>
              {t('component_attachment_modal_confirm_delete')}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <motion.button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                whileHover={!deleting ? { scale: 1.02 } : {}}
                whileTap={!deleting ? { scale: 0.97 } : {}}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 10, border: 'none',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  background: 'linear-gradient(135deg,#dc2626,#ef4444)',
                  color: 'white', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                }}
              >
                <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
                {deleting
                  ? <Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                  : <Trash2 style={{ width: 14, height: 14 }} />
                }
                {deleting ? t('component_attachment_modal_btn_deleting') : t('component_attachment_modal_btn_delete')}
              </motion.button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                style={{
                  padding: '8px 14px', borderRadius: 10,
                  border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                  background: 'transparent',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: 13, color: fgMuted,
                  fontFamily: 'inherit',
                }}
              >
                {t('lifespan_modal_btn_cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Zona de drop / sin attachment */}
        {!hasAttachment && (
          <div>
            <div
              role="button"
              tabIndex={0}
              onClick={onPickClick}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy) onPickClick(); }}
              onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 10, padding: '40px 20px',
                borderRadius: 18, marginBottom: 14,
                border: dragOver
                  ? '2px dashed #6366f1'
                  : (darkMode ? '2px dashed rgba(255,255,255,0.12)' : '2px dashed #cbd5e1'),
                background: dragOver
                  ? (darkMode ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.06)')
                  : (darkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc'),
                cursor: busy ? 'not-allowed' : 'pointer',
                textAlign: 'center',
                outline: 'none',
                transition: 'background 0.15s, border-color 0.15s',
              }}
            >
              <div
                style={{
                  width: 56, height: 56, borderRadius: 18,
                  background: darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Upload style={{ width: 26, height: 26, color: '#6366f1' }} />
              </div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: fgPrimary }}>
                {t('component_attachment_modal_empty_title')}
              </p>
              <p style={{ margin: 0, fontSize: 12.5, color: fgMuted, maxWidth: 380 }}>
                {t('component_attachment_modal_dropzone_label')}
              </p>
              <p style={{ margin: 0, fontSize: 11, color: fgFaint }}>
                {t('component_attachment_modal_accepted_types')}
              </p>
            </div>

            {selectedFile && (
              <div
                style={{
                  padding: '10px 14px', borderRadius: 12, marginBottom: 12,
                  background: darkMode ? 'rgba(255,255,255,0.04)' : '#f1f5f9',
                  border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}
              >
                {selectedFile.type === 'application/pdf'
                  ? <FileText style={{ width: 18, height: 18, color: '#0284c7' }} />
                  : <ImageIcon style={{ width: 18, height: 18, color: '#0284c7' }} />}
                <p style={{ margin: 0, fontSize: 12.5, color: fgPrimary, fontWeight: 600 }}>
                  {t('component_attachment_modal_selected_file', {
                    name: selectedFile.name,
                    size: formatFileSize(selectedFile.size, t),
                  })}
                </p>
              </div>
            )}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={onFileInputChange}
        />

        {/* Errores */}
        {validationError && (
          <div
            style={{
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.2)',
              marginBottom: 12,
            }}
          >
            <p style={{ margin: 0, fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
              • {validationError}
            </p>
          </div>
        )}
        {error && (
          <div
            style={{
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.2)',
              marginBottom: 12,
            }}
          >
            <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
          </div>
        )}

        {/* Botonera principal — Subir si hay archivo seleccionado y no hay attachment, o si hay attachment y se eligió un reemplazo */}
        {selectedFile && !confirmDelete && (
          <div style={{ display: 'flex', gap: 10 }}>
            <motion.button
              type="button"
              onClick={handleUpload}
              disabled={uploading || !!validationError}
              whileHover={!uploading && !validationError ? { scale: 1.02 } : {}}
              whileTap={!uploading && !validationError ? { scale: 0.97 } : {}}
              style={{
                flex: 1, padding: 13, borderRadius: 14, border: 'none',
                cursor: uploading || validationError ? 'not-allowed' : 'pointer',
                background: uploading || validationError
                  ? (darkMode ? 'rgba(255,255,255,0.08)' : '#cbd5e1')
                  : 'linear-gradient(135deg,#0ea5e9,#0284c7)',
                color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              {uploading
                ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : <Upload style={{ width: 16, height: 16 }} />
              }
              {uploading ? t('component_attachment_modal_btn_uploading') : t('component_attachment_modal_btn_upload')}
            </motion.button>
            <button
              type="button"
              onClick={() => { setSelectedFile(null); setValidationError(''); }}
              disabled={uploading}
              style={{
                padding: '13px 18px', borderRadius: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: 'transparent',
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14, color: fgMuted,
                fontFamily: 'inherit',
              }}
            >
              {t('lifespan_modal_btn_cancel')}
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default AttachmentModal;
